import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, MapPin, ArrowRight, ClipboardList, Bell, ShieldCheck, Plus, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { api, formatError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * /my-property — Homeowner mini-Passport view
 * Simple, warm, homeowner-friendly summary of their property maintenance history.
 */
export default function MyProperty() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/my-property');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.pnMyPropertySummary();
      setData(r.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="my-property-page">
      <Header />
      <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1A362D]">
              <Sparkles size={12} /> Homeowner · Your Property Passport
            </div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">My Property</h1>
            <p className="mt-3 max-w-2xl text-sm text-[#5C5B5A]">
              Your home's maintenance history — private, portable, and yours to share when you sell.
            </p>
          </div>
          <Link to="/portfolio" className="btn-ghost hidden sm:inline-flex" data-testid="my-property-manage">
            <Plus size={14} /> Add a property
          </Link>
        </div>

        {loading ? (
          <div className="mt-10 border border-dashed border-[#E5E4E0] bg-white p-12 text-center text-sm text-[#8A8988]" data-testid="my-property-loading">Loading…</div>
        ) : (
          <>
            {/* Stats */}
            <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatBox label="Properties" value={data.stats.properties} icon={Home} testId="stat-my-props" />
              <StatBox label="Open jobs" value={data.stats.jobs_open} icon={ClipboardList} testId="stat-my-open" />
              <StatBox label="Completed jobs" value={data.stats.jobs_completed} icon={FileText} testId="stat-my-completed" />
              <StatBox label="Warranty alerts" value={data.stats.warranty_alerts} icon={Bell} testId="stat-my-warranty" tone={data.stats.warranty_alerts > 0 ? 'accent' : 'default'} />
            </div>

            {/* Warranty alerts */}
            {data.warranty_alerts.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-[#1C1B1A]">Warranty alerts</h2>
                <div className="mt-3 space-y-2">
                  {data.warranty_alerts.map((w) => (
                    <div key={w.attachment_id} className="flex items-center justify-between border border-[#D96C4E] bg-[#D96C4E]/[0.06] p-4" data-testid={`warranty-alert-${w.attachment_id}`}>
                      <div>
                        <div className="font-semibold text-[#1C1B1A]">{w.job_title}</div>
                        <div className="mt-1 text-xs text-[#5C5B5A]">{w.filename} · expires {new Date(w.expires_on).toLocaleDateString('en-AU')}</div>
                      </div>
                      <div className={`text-sm font-semibold ${w.days_remaining < 0 ? 'text-[#D96C4E]' : 'text-[#1A362D]'}`}>
                        {w.days_remaining < 0 ? `Expired ${-w.days_remaining}d ago` : `${w.days_remaining}d remaining`}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Properties */}
            <section className="mt-10">
              <div className="mb-3 flex items-end justify-between">
                <h2 className="font-heading text-xl font-bold text-[#1C1B1A]">Your properties</h2>
              </div>
              {data.properties.length === 0 ? (
                <div className="border border-dashed border-[#E5E4E0] bg-white p-12 text-center" data-testid="my-property-empty">
                  <Home size={32} className="mx-auto text-[#8A8988]" />
                  <h3 className="mt-4 font-heading text-xl font-bold text-[#1C1B1A]">Add your first home</h3>
                  <p className="mt-2 text-sm text-[#5C5B5A]">Every job done on your property becomes part of its permanent history.</p>
                  <Link to="/portfolio" className="btn-primary mt-6 inline-flex" data-testid="my-property-first-add">
                    <Plus size={14} /> Add a property
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {data.properties.map((p) => (
                    <Link key={p.id} to="/portfolio" className="group border border-[#E5E4E0] bg-white p-5 transition-all hover:border-[#1A362D] hover:shadow-md" data-testid={`my-property-card-${p.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex h-9 w-9 items-center justify-center bg-[#1A362D] text-white">
                          <Home size={16} />
                        </div>
                        <div className="rounded-full border border-[#1A362D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#1A362D]">{p.property_type}</div>
                      </div>
                      <div className="mt-3 font-heading text-base font-bold text-[#1C1B1A]">{p.label || p.address_line}</div>
                      <div className="mt-1 flex items-center gap-1 text-xs text-[#8A8988]">
                        <MapPin size={11} /> {p.suburb} {p.postcode}
                      </div>
                      <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#1A362D] group-hover:gap-3">
                        Open Passport <ArrowRight size={12} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {/* Recent events */}
            {data.latest_events.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-[#1C1B1A]">Recent activity</h2>
                <ol className="mt-4 border-l-2 border-[#E5E4E0] pl-6" data-testid="my-property-events">
                  {data.latest_events.map((e, i) => (
                    <li key={i} className="relative mb-6">
                      <div className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#1A362D]" />
                      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">
                        {new Date(e.created_at).toLocaleDateString('en-AU')} · {e.category}
                      </div>
                      <div className="mt-1 font-semibold text-[#1C1B1A]">{e.title}</div>
                      <div className="mt-1 text-xs text-[#5C5B5A]">Stage: {e.stage}</div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* Trust badge */}
            <section className="mt-14 border border-[#E5E4E0] bg-white p-6 md:p-8">
              <div className="flex items-start gap-4">
                <ShieldCheck size={28} className="mt-1 flex-shrink-0 text-[#1A362D]" />
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D96C4E]">Trusted by neighbours</div>
                  <h3 className="mt-1 font-heading text-lg font-bold text-[#1C1B1A]">Your history stays yours.</h3>
                  <p className="mt-2 text-sm text-[#5C5B5A]">
                    Your Property Passport is private by default. Only you decide who sees it — the tradies who work on your home, or the buyer when you sell. Privacy Act 1988 (Cth) compliant.
                  </p>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, testId, tone }) {
  const accent = tone === 'accent' && value > 0;
  return (
    <div className={`border p-5 ${accent ? 'border-[#D96C4E] bg-[#D96C4E]/[0.06]' : 'border-[#E5E4E0] bg-white'}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">{label}</div>
        <Icon size={14} className={accent ? 'text-[#D96C4E]' : 'text-[#1A362D]'} />
      </div>
      <div className={`mt-2 font-heading text-3xl font-bold ${accent ? 'text-[#D96C4E]' : 'text-[#1C1B1A]'}`}>{value ?? '—'}</div>
    </div>
  );
}
